import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { WORKLIST_SECTION_IDS, computeSectionRowOffset } from "./worklist.service";

/**
 * Page-level state for "Today's worklist" (KAN-2553).
 *
 * The worklist is one page made of several sections, not several independent
 * widgets: the KAN-2555 mockup numbers its reminders 8–13, continuing the
 * numbering from the sections above, and the KAN-2554 mockup adds a
 * "0 of 30 done" bar and a chip row that count across every section. None of
 * that is expressible if each section fetches and numbers itself in isolation.
 *
 * So sections publish their row count here and read back where their own
 * numbering starts. Today there is exactly one section and the offset is always
 * 0 — the point is that adding the reminders section later is a registration,
 * not a rewrite.
 */

interface WorklistContextValue {
	/** Publish a section's row count. Safe to call on every render. */
	setSectionCount: (sectionId: string, count: number) => void;
	/** Where this section's row 1 sits in the page-wide numbering (0-based). */
	getRowOffset: (sectionId: string) => number;
	/** Rows across the whole worklist — the future "0 of N done" denominator. */
	totalCount: number;
}

const WorklistContext = createContext<WorklistContextValue>({
	setSectionCount: () => undefined,
	getRowOffset: () => 0,
	totalCount: 0,
});

export const WorklistProvider = ({ children }: { children: React.ReactNode }) => {
	const [counts, setCounts] = useState<Record<string, number>>({});

	const setSectionCount = useCallback((sectionId: string, count: number) => {
		setCounts((current) =>
			current[sectionId] === count ? current : { ...current, [sectionId]: count }
		);
	}, []);

	const value = useMemo<WorklistContextValue>(
		() => ({
			setSectionCount,
			getRowOffset: (sectionId: string) =>
				computeSectionRowOffset(sectionId, WORKLIST_SECTION_IDS, counts),
			totalCount: Object.values(counts).reduce((total, count) => total + count, 0),
		}),
		[counts, setSectionCount]
	);

	return <WorklistContext.Provider value={value}>{children}</WorklistContext.Provider>;
};

export const useWorklist = () => useContext(WorklistContext);
